/** @format */

import { useTranslation } from "react-i18next";
import { useEffect } from "react";

interface DialogProps {
	title: string;
	message: string;
	onConfirm: () => void;
	onCancel: () => void;
	btnDeleteMsg?: string;
}

const buttonStyle = "text-base text-center font-roboto rounded-md cursor-pointer h-10 py-[6px] px-[12px]";

export const ConfirmationDialog = ({ title, message, onConfirm, onCancel, btnDeleteMsg }: DialogProps) => {
	const { t } = useTranslation();

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onCancel();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onCancel]);

	return (
		<>
			<button className="fixed inset-0 bg-black/15 z-40" onClick={onCancel} />
			<div
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="clear-all-modal-title"
				aria-describedby="clear-all-modal-message"
				className="fixed w-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg max-w-125 overflow-hidden border border-(--public-color-paper-modal-border)"
			>
				<header
					id="clear-all-modal-title"
					className="flex p-4 w-full h-15.75 gap-2 capitalize text-[20px] text-(--public-color-text-filter) font-roboto border-b border-(--public-color-border-default) leading-[150%] align-middle items-center justify-between"
				>
					<span>{title}</span>
					<button onClick={onCancel} className="cursor-pointer align-middle">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2}
							stroke="currentColor"
							className="size-6"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>
				</header>
				<div className="flex flex-col h-30 p-4 gap-4">
					<p id="clear-all-modal-message" className="font-roboto text-[16px] text-(--public-color-text-default)">
						{message}
					</p>
					<p className="font-roboto text-[16px] text-(--public-color-text-default)">
						{t("confirmationDialog.cannotBeUndone")}
					</p>
				</div>
				<footer className="flex gap-2 w-full items-center justify-end p-3 h-15.75 border-t border-(--public-color-border-filter)">
					<button
						onClick={onCancel}
						className={`border items-center justify-center whitespace-nowrap min-w-18.75 border-(--public-color-menu-label) text-white bg-(--public-color-menu-label) ${buttonStyle}`}
					>
						{t("confirmationDialog.cancel")}
					</button>
					<button
						onClick={onConfirm}
						className={`flex flex-row gap-1 min-w-24 items-center justify-center whitespace-nowrap bg-white border border-(--public-color-menu-label) text-(--public-color-btn-clear-text) ${buttonStyle}`}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={1.5}
							stroke="#C21200"
							className="size-4.5"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
							/>
						</svg>

						{btnDeleteMsg}
					</button>
				</footer>
			</div>
		</>
	);
};
